import { useState, useEffect } from "react";
import Footer from "../../components/footer";
import Header from "../../components/protected/Header";
import { lastTime } from "../../components/utils/Time";
import { useTranslations } from "../../translations";
import { 
  RiUserLine, 
  RiMailLine, 
  RiMusic2Line, 
  RiHistoryLine, 
  RiBarChartLine,
  RiQuestionFill
} from "react-icons/ri";
import { storage } from "../../config/firebase";
import StudentActivity from "../../components/protected/activity/StudentActivity";
import { ref as sRef, getDownloadURL } from "firebase/storage";

const Dashboard = ({ values }) => {
  const [lastTimeConn, setLastTimeConn] = useState();
  const [timeConn, setTimeConn] = useState();
  const [numConne, setNumConne] = useState();
  const [apprenticeNameSrc, setApprenticeNameSrc] = useState("");
  const [congratulatoryPhraseSrc, setCongratulatoryPhraseSrc] = useState("");
  const { t } = useTranslations();

  useEffect(() => {
    if (!lastTime(values.learner)) {
      return;
    }
    const { date, diff, numConn } = lastTime(values.learner);
    setLastTimeConn(date);
    setNumConne(numConn);
    setTimeConn(diff);
  }, [values.learner]);

  useEffect(() => {
    const fetchValidUrl = async (rawUrl, setSrc) => {
      if (!rawUrl) return;
      if (rawUrl.includes("firebasestorage") && !rawUrl.includes("token=")) {
        try {
          const pathMatches = rawUrl.match(/\/o\/(.+?)\?/);
          if (pathMatches && pathMatches[1]) {
            const path = decodeURIComponent(pathMatches[1]);
            const validUrl = await getDownloadURL(sRef(storage, path));
            setSrc(validUrl);
            return;
          }
        } catch (error) {
          console.error("Error fetching valid URL:", error);
        }
      }
      setSrc(rawUrl);
    };

    const rawApprentice = values?.parent?.apprenticeNameLink || values?.learner?.apprenticeNameLink;
    const rawCongrat = values?.parent?.congratulatoryPhraseLink || values?.learner?.congratulatoryPhraseLink;

    fetchValidUrl(rawApprentice, setApprenticeNameSrc);
    fetchValidUrl(rawCongrat, setCongratulatoryPhraseSrc);
  }, [values]);

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content">
        <div className="d-flex justify-content-between align-items-center mb-5">
          <div className="fade-in">
            {/* Quién inició sesión: el representante (padre/tutor) */}
            <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
              <span className="dash-role-pill">{t("representative")}</span>
              {values?.parent?.email && (
                <span className="welcome-text mb-0" style={{ fontSize: "0.85rem", opacity: 0.75 }}>
                  {values.parent.email}
                </span>
              )}
            </div>
            <h1 className="dashboard-title mb-1 animate__animated animate__fadeInDown">{t("dashboard")}</h1>
            {/* A quién se está monitoreando: el aprendiz */}
            <p className="welcome-text mb-0">
              {t("monitoringLearner")}{" "}
              <span className="fw-bold animate__animated animate__fadeIn" style={{ color: "var(--pink-bright)" }}>
                {values?.learner?.learnerName}
              </span>
            </p>
          </div>
        </div>

        <div className="row g-4">
          {/* Profile Card */}
          <div className="col-lg-4">
            <div className="modern-card h-100 p-4">
              {/* Deja claro que esta tarjeta es del aprendiz, no del representante */}
              <div className="text-center mb-3">
                <span className="dash-learner-badge">{t("learnerBadge")}</span>
              </div>
              <div className="text-center mb-4">
                <div className="position-relative d-inline-block p-1 rounded-circle" style={{ background: 'linear-gradient(45deg, var(--pink-bright), var(--plum-bg))' }}>
                  <img
                    src={values?.learner?.profileImageLink || values?.learner?.profileImage || values?.learner?.avatar || "/assets/img/noprofil.png"}
                    className="rounded-circle shadow-sm border border-4 border-white"
                    alt="Profile"
                    style={{ height: "140px", width: "140px", objectFit: "cover" }}
                    onError={(e) => { e.target.src = "/assets/img/noprofil.png" }}
                  />
                  <div className="position-absolute bottom-0 end-0 bg-success rounded-circle border border-4 border-white" style={{ width: "26px", height: "26px", bottom: '10px', right: '10px' }}></div>
                </div>
                <h3 className="mt-3 mb-1 fw-bold color-plum fs-4">{values?.learner?.learnerName}</h3>
                <div className="d-inline-block px-3 py-1 mt-1 fs-6 fw-bold" style={{ backgroundColor: 'var(--pastel-pink)', color: 'var(--pink-bright)', borderRadius: '50px' }}>
                  {values?.learner?.estudiandoGrado?.toUpperCase()}
                </div>
                <p className="text-muted small mt-3 mb-0">{t("learnerProfileHint")}</p>
              </div>

              <div className="info-list mt-4">
                <div className="d-flex align-items-center gap-3 p-3 mb-3" style={{ background: 'rgba(99, 45, 67, 0.03)', borderRadius: '20px', border: '1px solid rgba(99, 45, 67, 0.05)' }}>
                  <div className="p-3 bg-white rounded-4 shadow-sm text-plum">
                    <RiMailLine size={22} />
                  </div>
                  <div className="overflow-hidden">
                    <p className="small text-muted mb-0 fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>{t("email")}</p>
                    <p className="mb-0 fw-bold color-plum text-truncate small">{values?.learner?.email}</p>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 p-3" style={{ background: 'rgba(99, 45, 67, 0.03)', borderRadius: '20px', border: '1px solid rgba(99, 45, 67, 0.05)' }}>
                  <div className="p-3 bg-white rounded-4 shadow-sm text-plum">
                    <RiUserLine size={22} />
                  </div>
                  <div>
                    <p className="small text-muted mb-0 fw-bold text-uppercase" style={{ letterSpacing: '0.5px' }}>{t("username")}</p>
                    <p className="mb-0 fw-bold color-plum small">{values?.learner?.username}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Media & Stats Card */}
          <div className="col-lg-8">
            <div className="modern-card h-100 p-4">
              <div className="d-flex align-items-center gap-3 mb-4">
                <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(235, 33, 120, 0.1)', color: 'var(--pink-bright)' }}>
                  <RiMusic2Line size={28} />
                </div>
                <div>
                  <h4 className="fw-bold mb-0 color-plum">{t("personalAudio")}</h4>
                  <p className="text-muted small mb-0">{t("personalAudioDescription") || "Tu voz y frases de motivación personalizadas"}</p>
                </div>
              </div>
              
              <div className="audio-grid p-4 mb-4" style={{ background: 'linear-gradient(135deg, rgba(212, 230, 241, 0.4), rgba(212, 230, 241, 0.2))', borderRadius: '30px', border: '1px solid var(--pastel-blue)' }}>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="bg-white p-3 rounded-4 shadow-sm border border-light">
                      <label className="audio-label">{t("apprenticeName")}</label>
                      <audio 
                        src={apprenticeNameSrc} 
                        controls 
                        className="w-100 custom-audio" 
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="bg-white p-3 rounded-4 shadow-sm border border-light">
                      <label className="audio-label">{t("congratulatoryPhrase")}</label>
                      <audio 
                        src={congratulatoryPhraseSrc} 
                        controls 
                        className="w-100 custom-audio" 
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <div className="stats-box p-4 h-100 d-flex align-items-center gap-3" style={{ background: 'white', borderRadius: '24px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 15px rgba(0,0,0,0.02)' }}>
                    <div className="p-3 rounded-4 bg-light text-muted">
                      <RiHistoryLine size={24} />
                    </div>
                    <div>
                      <p className="text-muted mb-0 small fw-bold text-uppercase">{t("lastLogin")}</p>
                      <p className="fw-bold mb-0 color-plum">{lastTimeConn || "---"}</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="stats-box p-4 h-100 d-flex align-items-center justify-content-between" style={{ background: 'white', border: '2px dashed var(--pastel-pink)', borderRadius: '24px' }}>
                    <div className="d-flex align-items-center gap-3">
                      <div className="p-3 rounded-4" style={{ background: 'rgba(235, 33, 120, 0.05)', color: 'var(--pink-bright)' }}>
                        <RiBarChartLine size={24} />
                      </div>
                      <div>
                        <p className="text-muted mb-0 small fw-bold text-uppercase">{t("totalConnections")}</p>
                        <p className="fw-bold mb-0 color-plum lh-1" style={{ fontSize: '1.8rem' }}>{numConne || 0}</p>
                      </div>
                    </div>
                    <div className="d-none d-sm-block">
                      <div className="badge rounded-pill px-3 py-2 bg-pink opacity-75">Active</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Actividad del aprendiz en la app (eventos), solo si ya está vinculado */}
          {values?.learner?.id && (
            <div className="col-12">
              <div className="modern-card p-4">
                <h4 className="fw-bold mb-3 color-plum">📈 {t("actTitle")} {values.learner.learnerName}</h4>
                <StudentActivity uid={values.learner.id} />
              </div>
            </div>
          )}
        </div>

        <Footer logged={values.logged} />
      </main>

      <style>{`
        .color-plum { color: var(--plum-bg) !important; }
        .text-plum { color: var(--plum-bg) !important; }

        /* Etiqueta del representante (quién inició sesión) */
        .dash-role-pill {
          display: inline-flex;
          align-items: center;
          background: var(--plum-bg);
          color: #fff;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          padding: 4px 12px;
          border-radius: 50px;
        }

        /* Etiqueta de la tarjeta de perfil (a quién se monitorea) */
        .dash-learner-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: linear-gradient(135deg, var(--pink-bright), var(--plum-bg));
          color: #fff;
          font-size: 0.72rem;
          font-weight: 800;
          letter-spacing: 0.8px;
          text-transform: uppercase;
          padding: 5px 16px;
          border-radius: 50px;
          box-shadow: 0 6px 16px rgba(235, 33, 120, 0.25);
        }
        
        .custom-audio {
          height: 42px;
          border-radius: 50px;
          border: 1px solid rgba(0,0,0,0.05);
        }
        
        .custom-audio::-webkit-media-controls-panel {
          background-color: #fcfcfc;
        }

        .custom-audio::-webkit-media-controls-play-button {
           background-color: var(--pink-bright);
           border-radius: 50%;
        }
        
        .audio-label {
          color: var(--plum-bg);
          font-weight: 800;
          font-size: 0.7rem;
          letter-spacing: 1.2px;
          opacity: 0.7;
          text-transform: uppercase;
          margin-bottom: 12px;
          display: block;
        }
        
        .info-list p { margin-bottom: 0; }
        
        .stats-box {
          transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
          border: 1px solid rgba(99, 45, 67, 0.05) !important;
        }
        
        .stats-box:hover {
          transform: translateY(-8px);
          box-shadow: 0 15px 35px rgba(99, 45, 67, 0.1) !important;
          border-color: var(--pink-bright) !important;
          background: linear-gradient(to bottom, #ffffff, #fff9fb) !important;
        }

        .badge.bg-pink {
          background-color: var(--pastel-pink) !important;
          color: var(--pink-bright) !important;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
